// /client/src/utils/v3LeadsHelpers.js
//
// Phase 7: pure, framework-independent helpers for the V3 Leads frontend
// integration. Plain CommonJS on purpose so the exact same file can be
// `require()`d both by React components (webpack/CRA supports CJS require
// fine) AND by a plain-Node test script (client/scripts/testV3LeadsFrontend.js)
// without any build step or extra test-framework dependency — this keeps
// frontend testing consistent with this repo's existing
// `node scripts/testXxx.js` convention (no @testing-library/react is
// installed in this project).
//
// No DOM, no React, no network calls — safe to unit test directly.

const JOB_ID_STORAGE_KEY = 'v3LeadsActiveJobId';

// ---------------------------------------------------------------------------
// Feature flag (rollout/rollback switch, NOT a permanent user-facing choice).
// ---------------------------------------------------------------------------

function isV3LeadsEnabled() {
  return process.env.REACT_APP_V3_LEADS_ENABLED === 'true';
}

// ---------------------------------------------------------------------------
// Job status helpers
// ---------------------------------------------------------------------------

const TERMINAL_JOB_STATUSES = ['completed', 'completed_with_errors', 'cancelled', 'failed'];

function isTerminalJobStatus(status) {
  return TERMINAL_JOB_STATUSES.includes(status);
}

function isTerminalWorkflow(job) {
  if (job?.preparationState === 'preparing') return false;
  return job?.verifyRemainingEffectiveEmails
    ? ['complete', 'unresolved', 'cancelled'].includes(job.workflow?.stage)
    : isTerminalJobStatus(job?.status);
}

function isActiveJobStatus(status) {
  return status === 'queued' || status === 'running' || status === 'cancelling';
}

// ---------------------------------------------------------------------------
// Effective-lead row helpers (Phase 6 rows: one row per email, stable lead_id)
// ---------------------------------------------------------------------------

/** The stable id Phase 6 already assigns — never synthesize a new one client-side. */
function getLeadRowId(row) {
  return row && row.lead_id != null ? String(row.lead_id) : null;
}

/** Case-insensitive dedupe by email, preserving first-occurrence order. Backend rows are never mutated. */
function dedupeLeadRowsByEmail(rows, enabled) {
  const list = Array.isArray(rows) ? rows : [];
  if (!enabled) return { rows: list, duplicatesRemoved: 0 };
  const seen = new Set();
  const out = [];
  for (const row of list) {
    const key = String(row.email || '').toLowerCase();
    if (!key || !seen.has(key)) {
      if (key) seen.add(key);
      out.push(row);
    }
  }
  return { rows: out, duplicatesRemoved: list.length - out.length };
}

/**
 * Normalizes a ZeroBounce status value for filter comparison, matching
 * EmailsTable's existing convention exactly (lowercase, '-' -> '_'). A
 * missing/null status is treated as 'unverified' — never crashes, never
 * hidden when the filter is 'all'.
 */
function normalizeZbStatusForFilter(status) {
  if (!status) return 'unverified';
  return String(status).toLowerCase().replace(/-/g, '_');
}

/** Filters effective lead rows by the user-selected ZeroBounce status. 'all' never filters. */
function filterLeadRowsByZbStatus(rows, statusFilter) {
  const list = Array.isArray(rows) ? rows : [];
  if (!statusFilter || statusFilter === 'all') return list;
  const target = normalizeZbStatusForFilter(statusFilter);
  return list.filter((row) => normalizeZbStatusForFilter(row.zerobounce_status) === target);
}

// ---------------------------------------------------------------------------
// CSV export (effective/normalized rows) — Phase 6 columns only, never raw
// provider payloads/secrets.
// ---------------------------------------------------------------------------

// Canonical export: preserve the detailed column order, with explicit street
// after mailing_address. Header aliases never change the selected effective row.
const EFFECTIVE_LEADS_CSV_COLUMNS = [
  { key: 'lead_id', header: 'email_verification_id' },
  { key: 'owner_unique_id', header: 'unique_id' },
  { key: 'cad_owner_name', header: 'cad_owner_name' },
  { key: 'cad_owner_type', header: 'cad_owner_type' },
  { key: 'full_name', header: 'full_name_bd' },
  { key: 'first_name', header: 'first_name' },
  { key: 'last_name', header: 'last_name' },
  { key: 'contact_role', header: 'contact_role' },
  { key: 'name_match_score', header: 'name_match_score' },
  { key: 'final_match_status', header: 'final_match_status' },
  { key: 'enrichment_source', header: 'source' },
  { key: 'email', header: 'email' },
  { key: 'zerobounce_status', header: 'zb_status' },
  { key: 'zerobounce_sub_status', header: 'zb_sub_status' },
  { key: 'county_name', header: 'county_name' },
  { key: 'legal_address', header: 'legal_address' },
  { key: 'mailing_address', header: 'mailing_address' },
  { key: 'street', header: 'street' },
  { key: 'city', header: 'city' },
  { key: 'state', header: 'state' },
  { key: 'zip', header: 'zip' },
  { key: 'acres', header: 'acres' },
  { key: 'land_id', header: 'land_id' },
  { key: 'owned_lands', header: 'owned_lands' },
  { key: 'id_list', header: 'id_list' },
  { key: 'acres_list', header: 'acres_list' },
  { key: 'market_value_list', header: 'market_value_list' },
  { key: 'owner_tax_year_list', header: 'owner_tax_year_list' },
  { key: 'deed_date_list', header: 'deed_date_list' },
  { key: 'mobile_1', header: 'mobile_1' },
  { key: 'mobile_2', header: 'mobile_2' },
  { key: 'mobile_3', header: 'mobile_3' },
  { key: 'mobile_4', header: 'mobile_4' },
  { key: 'mobile_numbers', header: 'mobile_numbers' },
  { key: 'mobile_count', header: 'mobile_count' },
  { key: 'mobile_source', header: 'mobile_source' },
];

function csvEscape(value) {
  if (Array.isArray(value)) return `"${value.join(';').replace(/"/g, '""')}"`;
  if (value === null || value === undefined) return '';
  const str = String(value);
  return `"${str.replace(/"/g, '""')}"`;
}

function buildEffectiveLeadsCsv(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const header = EFFECTIVE_LEADS_CSV_COLUMNS.map((c) => c.header).join(',');
  const body = list.map((row) => EFFECTIVE_LEADS_CSV_COLUMNS.map((c) => csvEscape(row[c.key])).join(','));
  return [header, ...body].join('\n');
}

// ---------------------------------------------------------------------------
// Persistent job-id storage (survives page refresh). Versioned key so a
// future incompatible shape change can be rolled out without reading stale data.
// ---------------------------------------------------------------------------

function storeActiveJobId(jobId, storage) {
  const s = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  if (!s) return;
  if (jobId) s.setItem(JOB_ID_STORAGE_KEY, jobId);
  else s.removeItem(JOB_ID_STORAGE_KEY);
}

function readActiveJobId(storage) {
  const s = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  if (!s) return null;
  return s.getItem(JOB_ID_STORAGE_KEY) || null;
}

function clearActiveJobId(storage) {
  storeActiveJobId(null, storage);
}

module.exports = {
  isTerminalWorkflow,
  JOB_ID_STORAGE_KEY,
  isV3LeadsEnabled,
  TERMINAL_JOB_STATUSES,
  isTerminalJobStatus,
  isActiveJobStatus,
  getLeadRowId,
  dedupeLeadRowsByEmail,
  normalizeZbStatusForFilter,
  filterLeadRowsByZbStatus,
  EFFECTIVE_LEADS_CSV_COLUMNS,
  buildEffectiveLeadsCsv,
  storeActiveJobId,
  readActiveJobId,
  clearActiveJobId,
};
